import { Button, Heading } from '@mm/components';
import { useViewActions } from '@mm/state/actions/viewActions';
import React from 'react';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import styles from './MyLeadsFiltersSlideout.module.css';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { MyLeadsFiltersPanel } from '../../../../features/beta/modules/Contacts/MyLeadsFiltersPanel/MyLeadsFiltersPanel';
import { MyLeadsSearchQuery } from '@mm/state/interfaces/myLeads';
import { CloseOutlined } from '@ant-design/icons';
import { MyLeadsFilterBar } from '../../../../features/beta/modules/Contacts/MyLeadsFilterBar/MyLeadsFilterBar';
import AudiencesModel, { TagsFilters } from '@mm/state/model/AudiencesModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useSelector } from 'react-redux';
import { selectContacts } from '@mm/state/selectors/contactsSelector';
import { useCreateFilterParams } from '../../../../features/beta/modules/Contacts/use-create-filter-params';
import { Spin } from 'antd';
import { useCreateMyLeadsAudience } from '../../../../features/beta/modules/Contacts/hooks/use-create-my-leads-audience';
import { useFilterMyLeads } from '../../../../features/beta/modules/Contacts/hooks/use-filter-my-leads';

export type MyLeadsFiltersSlideoutProps = {
  title?: string;
  buttonName?: string;
  withSaveAsNew?: boolean;
  filterParams?: MyLeadsSearchQuery;
  setFilterParams?: (params: MyLeadsSearchQuery) => void;
  activeAudience?: AudiencesModel;
  onSave?: (audienceId: number) => void;
};

export const MyLeadsFiltersSlideout = ({
  title = 'Build an Audience',
  buttonName,
  withSaveAsNew = true,
  activeAudience,
  filterParams,
  setFilterParams,
  onSave,
}: MyLeadsFiltersSlideoutProps) => {
  const { pagination } = useSelector(selectContacts);
  const viewActions = useViewActions();

  const onClose = () => onBeforeClose(filterParams, localFilterParams);

  const { setRef, onBeforeClose } = useSlideoutOnClose(onClose);

  const close = async () => {
    try {
      await onClose();
      viewActions.hideSlideoutMenu();
    } catch (err) {}
  };

  const contactsCount = pagination?.total?.toLocaleString();

  const {
    filterParams: localFilterParams,
    setFilterParams: setLocalFilterParams,
  } = useCreateFilterParams(filterParams);

  const updateFilterParams = (params: MyLeadsSearchQuery) => {
    setLocalFilterParams(params);
  };

  const { createMyLeadsAudience, updateMyLeadsAudience } =
    useCreateMyLeadsAudience();
  const { openCreateMyLeadsAudienceModal } = useModalActions();

  const { busy } = useFilterMyLeads({
    fetchAttributes: true,
    filterParams: localFilterParams,
  });

  const onCreate = async () => {
    const params = {
      ...localFilterParams,
      esFilters: localFilterParams.query,
      tagsFilters: localFilterParams.tagsFilters as TagsFilters,
    };

    await openCreateMyLeadsAudienceModal({
      onSave: audienceName => {
        createMyLeadsAudience({
          params: {
            ...params,
            name: audienceName,
          },
        });
        viewActions.hideSlideoutMenu();
      },
    });
  };

  const onUpdate = async () => {
    await updateMyLeadsAudience(localFilterParams, activeAudience);
    setFilterParams?.(localFilterParams);
    if (activeAudience?.id) {
      await onSave?.(activeAudience?.id);
    }

    viewActions.hideSlideoutMenu();
  };

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <div className={styles.headerContent}>
        <Heading className={styles.headerTitle} size={3}>
          {title}
        </Heading>
        <CloseOutlined onClick={close} className={styles.closeIcon} />
        <div className={styles.headerButtons}>
          {!activeAudience && (
            <Spin spinning={busy} size="small">
              <Button
                css={{ container: styles.button }}
                onClick={onCreate}
                disabled={busy}
              >
                {buttonName || `Create an Audience (${contactsCount})`}
              </Button>
            </Spin>
          )}
          {activeAudience && (
            <>
              <Button css={{ container: styles.button }} onClick={onUpdate}>
                {buttonName || `Save (${contactsCount})`}
              </Button>
              {withSaveAsNew && (
                <Button
                  color="secondary-blue"
                  css={{ container: styles.button }}
                  onClick={onCreate}
                >
                  Save as a new Audience
                </Button>
              )}
            </>
          )}
        </div>
      </div>
      <SlideoutMenu.Content className={styles.content}>
        <MyLeadsFilterBar
          css={{ container: styles.filtersBar }}
          filterParams={localFilterParams}
          setFilterParams={updateFilterParams}
        />
        <MyLeadsFiltersPanel
          css={{
            container: styles.filtersPanel,
            suggestedAudiences: styles.suggestedAudiences,
            filters: styles.filters,
          }}
          filterParams={localFilterParams}
          setFilterParams={updateFilterParams}
          busy={busy}
        />
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};
